import React from 'react';
import PropTypes from 'prop-types';
import Card from './card';
import { FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';

class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false, error: null, errorInfo: null };
	}

	static getDerivedStateFromError(error) {
		// Update state so the next render will show the fallback UI
		return { hasError: true };
	}

	componentDidCatch(error, errorInfo) {
		// Log error details for debugging
		this.setState({
			error: error,
			errorInfo: errorInfo
		});
		
		// Log to console in development
		if (process.env.NODE_ENV === 'development') {
			console.error('ErrorBoundary caught an error:', error, errorInfo);
		}
	}

	handleRetry = () => {
		this.setState({ hasError: false, error: null, errorInfo: null });
	};

	render() {
		if (this.state.hasError) {
			// Fallback UI
			return (
				<Card className="overflow-hidden h-full">
					<div className="p-8 h-full flex flex-col justify-center items-center text-center">
						<div className="rounded-full p-4 bg-red-100 mb-4">
							<FiAlertTriangle className="h-8 w-8 text-red-600" />
						</div>
						
						<h3 className="text-lg font-glacial text-gray-900 mb-2">
							{this.props.title || 'Something went wrong'}
						</h3>
						
						<p className="text-sm font-montserrat-regular text-gray-600 mb-6 max-w-sm">
							{this.props.message || 'This component encountered an error and couldn\'t be displayed.'}
						</p>
						
						{this.props.showRetry && (
							<button
								onClick={this.handleRetry}
								className="inline-flex items-center px-4 py-2 bg-primary text-white text-sm font-montserrat-medium rounded-lg hover:bg-primary/90 transition-colors"
							>
								<FiRefreshCw className="h-4 w-4 mr-2" />
								Try Again
							</button>
						)}
						
						{process.env.NODE_ENV === 'development' && this.state.error && (
							<details className="mt-6 text-left w-full">
								<summary className="text-xs font-montserrat-medium text-gray-500 cursor-pointer">
									Error Details (Development)
								</summary>
								<pre className="text-xs text-red-600 mt-2 p-2 bg-red-50 rounded overflow-auto max-h-32">
									{this.state.error.toString()}
									{this.state.errorInfo.componentStack}
								</pre>
							</details>
						)}
					</div>
				</Card>
			);
		}

		return this.props.children;
	}
}

ErrorBoundary.propTypes = {
	children: PropTypes.node.isRequired,
	title: PropTypes.string,
	message: PropTypes.string,
	showRetry: PropTypes.bool
};

ErrorBoundary.defaultProps = {
	showRetry: true
};

export default ErrorBoundary;